import { Component, computed, inject, input } from '@angular/core';
import {
  CardBodyComponent,
  CardComponent,
  CardHeaderComponent,
  CustomerCardComponent,
  CustomerContactItem,
  CustomerOpportunityItem,
  StateMessageComponent
} from "@groundworks/ngx-groundworks-ui";
import { ConfigService } from '../../config.service';
import { OpportunityInfo } from '../../sdk/sdk';

export const OPPORTUNITY_FUNNEL_PHASES: { steps: string[] }[] = [
  { steps: ['Opportunity'] },
  { steps: ['Booked', 'Issued'] },
  { steps: ['Quoted', 'Sold'] },
  { steps: ['Scheduled', 'Installing'] },
  { steps: ['Completed'] },
];

export const OPPORTUNITY_FUNNEL_STEPS: { label: string }[] = OPPORTUNITY_FUNNEL_PHASES.flatMap(
  (phase) => phase.steps,
).map((label) => ({ label }));

type ThemeColor = 'orange' | 'purple' | 'aqua' | 'green-tint';

function themeColorForType(typeName: string | undefined): ThemeColor {
  switch (typeName?.toLowerCase()) {
    case 'recurring':
      return 'orange';
    case 'repeat':
      return 'purple';
    default:
      return 'aqua';
  }
}

export interface CustomerCardView {
  customerName: string;
  customerId: string;
  customerSince: string;
  phone: string;
  email: string;
  address: string;
  associatedContacts: CustomerContactItem[];
  opportunities: CustomerOpportunityItem[];
}

function contactFullName(contact: { firstName?: string; lastName?: string }): string {
  return `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim();
}

function toFullAddress(opportunity: OpportunityInfo): string {
  const address = opportunity.address;
  if (!address) {
    return '';
  }
  return `${address.streetAddress}, ${address.city}, ${address.state} ${address.postalCode}`;
}

export function toCustomerCardView(opportunity: OpportunityInfo): CustomerCardView {
  const contacts = opportunity.contacts ?? [];
  const primary = contacts.find((contact) => contact.isPrimary) ?? contacts[0];

  const associatedContacts: CustomerContactItem[] = contacts
    .filter((contact) => contact !== primary)
    .map((contact) => ({
      name: contactFullName(contact),
      phone: contact.phone,
      email: contact.email,
    }));

  const opportunityItem: CustomerOpportunityItem = {
    typeName: opportunity.opportunityTypeName ?? '',
    themeColor: themeColorForType(opportunity.opportunityTypeName),
    statusBadge: opportunity.isActive ? 'Active' : undefined,
    statusBadgeType: 'success',
    date: opportunity.createdAt?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
    steps: OPPORTUNITY_FUNNEL_STEPS,
    currentStep: opportunity.phase?.name,
    furthestCompletedStep: opportunity.furthestPhaseName,
    isCommercial: opportunity.address?.isCommercial ?? false,
    commercialToggleReadonly: true,
    isClickable: false,
    isActive: opportunity.isActive,
  };

  return {
    customerName: primary ? contactFullName(primary) : '',
    customerId: opportunity.customerId != null ? String(opportunity.customerId) : '',
    customerSince: opportunity.customerSince?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) ?? '',
    phone: primary?.phone ?? '',
    email: primary?.email ?? '',
    address: toFullAddress(opportunity),
    associatedContacts,
    opportunities: [opportunityItem],
  };
}

@Component({
  selector: 'app-selected-opportunity',
  imports: [
    CardBodyComponent,
    CardComponent,
    CustomerCardComponent,
    StateMessageComponent,
    CardHeaderComponent,
  ],
  templateUrl: './selected-opportunity.component.html',
})
export class SelectedOpportunityComponent {
  readonly configService = inject(ConfigService);
  opportunity = input<OpportunityInfo | undefined>(undefined);
  readonly isLoadingInput = input<boolean>(false);
  readonly hasErrorInput = input<boolean>(false);

  readonly state = computed(() => {
    if (this.isLoadingInput()) {
      return 'loading';
    }
    if (this.hasErrorInput()) {
      return 'error';
    }
    if (!this.opportunity()) {
      return 'no-matches';
    }
    return 'matches';
  });

  readonly customerView = computed(() => {
    const opportunity = this.opportunity();
    return opportunity ? toCustomerCardView(opportunity) : undefined;
  });

  getState(): string {
    return this.state();
  }

  onDeselect() {
    window.close();
  }

  onAddressClick() {
    const homepageUrl = this.configService.homeUrl;
    const address = this.opportunity()?.address?.streetAddress;
    const address2 = this.opportunity()?.address?.streetAddress2;
    const city = this.opportunity()?.address?.city;
    const state = this.opportunity()?.address?.state;
    const postalCode = this.opportunity()?.address?.postalCode;

    const params = new URLSearchParams();
    if (address) params.set('streetAddress', address);
    if (address2) params.set('streetAddress2', address2);
    if (city) params.set('city', city);
    if (state) params.set('st', state);
    if (postalCode) params.set('postalCode', postalCode);

    const url = `${homepageUrl}/?${params.toString()}`;
    window.open(url, '_blank');
  }
}
